import { useNavigate } from "react-router-dom";
import { Search, Settings, Trash2 } from "lucide-react";
import { AppShell } from "@/components/layout/app-shell";
import { NotificationBell } from "@/components/layout/notification-bell";
import { UserMenu } from "@/components/layout/user-menu";
import type { RailNavItem } from "@/components/layout/sidebar-rail";
import { SearchOverlay } from "@/components/layout/search-overlay";
import {
  SearchOverlayProvider,
  useSearchOverlay,
} from "@/components/layout/search-overlay-context";
import {
  HelpWidgetProvider,
  useHelpWidget,
} from "@/components/layout/help-widget-context";
import { HelpWidget } from "@/components/layout/help-widget";
import { useSearchTrigger } from "@/components/layout/use-search-trigger";
import { useAuth, useLogout } from "@/hooks/use-auth";
import { roleDisplayName } from "@/lib/api-types";

export default function AdminLayout() {
  return (
    // Provider sits above AppShell so both the rail (Search icon) AND deeper
    // pages (project-detail / folder-detail search buttons) share the same
    // overlay state.
    <SearchOverlayProvider>
      <HelpWidgetProvider>
        <AdminLayoutInner />
        <AdminOverlayHost />
        <HelpWidget />
      </HelpWidgetProvider>
    </SearchOverlayProvider>
  );
}

function AdminLayoutInner() {
  const { user, role, workspaceName } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();

  // Rail Search icon: opens overlay on desktop, navigates to /search on mobile.
  const { openWidget } = useHelpWidget();
  const triggerSearch = useSearchTrigger("/admin/search");

  const NAV: RailNavItem[] = [
    { kind: "action", label: "Search", icon: Search, onClick: triggerSearch },
    { kind: "link", to: "/admin/trash", label: "Trash", icon: Trash2 },
    {
      kind: "custom",
      key: "notifications",
      render: () => <NotificationBell to="/admin/notifications" />,
    },
    { kind: "link", to: "/admin/settings", label: "Workspace settings", icon: Settings },
  ];

  return (
    <AppShell
      onOpenHelp={() => openWidget("help")}
      onOpenFeatureRequest={() => openWidget("message", "Feature request")}
      brandTo="/admin/projects"
      primaryNav={NAV}
      searchPlaceholder="Search projects…"
      projectsBasePath="/admin/projects"
      userMenu={
        <UserMenu
          name={user?.name || user?.email || "Account"}
          email={user?.email}
          roleLabel={roleDisplayName(role)}
          workspaceName={workspaceName ?? undefined}
          settingsTo="/admin/settings"
          isLoggingOut={logout.isPending}
          onLogout={() =>
            logout.mutate(undefined, { onSuccess: () => navigate("/login", { replace: true }) })
          }
        />
      }
    />
  );
}

function AdminOverlayHost() {
  const { open, scope, close } = useSearchOverlay();
  return <SearchOverlay open={open} onClose={close} scope={scope} basePath="/admin" />;
}
